/**
 * Single place that reads `process.env` for the browser app.
 * Create React App inlines `REACT_APP_*` at build time. Do not read
 * `process.env` elsewhere — import `env` from this module.
 *
 * Secrets that must stay off the client (Razorpay key secret, webhook
 * secret) belong in Cloud Functions config, not here.
 */

const ENV_ALIASES = {
  apiKey: ["REACT_APP_FIREBASE_API_KEY", "REACT_APP_API_KEY"],
  authDomain: ["REACT_APP_FIREBASE_AUTH_DOMAIN", "REACT_APP_AUTH_DOMAIN"],
  databaseURL: ["REACT_APP_FIREBASE_DATABASE_URL", "REACT_APP_DATABASE_URL"],
  projectId: ["REACT_APP_FIREBASE_PROJECT_ID", "REACT_APP_PROJECT_ID"],
  storageBucket: ["REACT_APP_FIREBASE_STORAGE_BUCKET", "REACT_APP_STORAGE_BUCKET"],
  messagingSenderId: [
    "REACT_APP_FIREBASE_MESSAGING_SENDER_ID",
    "REACT_APP_MESSAGING_SENDER_ID",
  ],
  appId: ["REACT_APP_FIREBASE_APP_ID", "REACT_APP_APP_ID"],
  measurementId: ["REACT_APP_FIREBASE_MEASUREMENT_ID", "REACT_APP_MEASUREMENT_ID"],
  /** Web Push (FCM) — Firebase Console → Project settings → Cloud Messaging → Web Push certificates */
  vapidKey: ["REACT_APP_FIREBASE_VAPID_KEY"],
  logLevel: ["REACT_APP_LOG_LEVEL"],
  cryptoSecret: ["REACT_APP_CRYPTO_SECRET"],
  sentryDsn: ["REACT_APP_SENTRY_DSN"],
  sentryEnvironment: ["REACT_APP_SENTRY_ENVIRONMENT"],
  sentryRelease: ["REACT_APP_SENTRY_RELEASE"],
  commitSha: ["REACT_APP_COMMIT_SHA"],
  razorpayKeyId: ["REACT_APP_RAZORPAY_KEY_ID"],
  razorpayOrderUrl: ["REACT_APP_RAZORPAY_ORDER_URL"],
  razorpayVerifyUrl: ["REACT_APP_RAZORPAY_VERIFY_URL"],
  publicContactUrl: ["REACT_APP_PUBLIC_CONTACT_URL"],
  refreshLandingSummaryUrl: ["REACT_APP_REFRESH_LANDING_SUMMARY_URL"],
  writerCheckoutUrl: ["REACT_APP_WRITER_CHECKOUT_URL"],
};

const getFirstDefinedEnv = (keys) => {
  for (const key of keys) {
    const value = process.env[key];
    if (value && String(value).trim()) return String(value).trim();
  }
  return "";
};

const REQUIRED_ENV_ALIASES = [
  ENV_ALIASES.apiKey,
  ENV_ALIASES.authDomain,
  ENV_ALIASES.databaseURL,
  ENV_ALIASES.projectId,
  ENV_ALIASES.storageBucket,
  ENV_ALIASES.messagingSenderId,
  ENV_ALIASES.appId,
];

const getMissingEnvKeys = () =>
  REQUIRED_ENV_ALIASES
    .filter((aliases) => !getFirstDefinedEnv(aliases))
    .map((aliases) => aliases[0]);

export const nodeEnv = process.env.NODE_ENV || "development";
export const isProduction = nodeEnv === "production";
export const isDevelopment = nodeEnv === "development";

export const validateRequiredEnv = () => {
  const missing = getMissingEnvKeys();
  if (missing.length === 0) return;

  const message =
    `[env] Missing required environment variables: ${missing.join(", ")}. ` +
    "Create a .env file in the project root (see .env.example) and restart the dev server.";

  // Fail fast in all environments: Firebase initialization throws an opaque error when URLs are empty.
  throw new Error(message);
};

const writerCheckoutUrl = getFirstDefinedEnv(ENV_ALIASES.writerCheckoutUrl).replace(/\/$/, "");

export const env = {
  apiKey: getFirstDefinedEnv(ENV_ALIASES.apiKey),
  authDomain: getFirstDefinedEnv(ENV_ALIASES.authDomain),
  databaseURL: getFirstDefinedEnv(ENV_ALIASES.databaseURL),
  projectId: getFirstDefinedEnv(ENV_ALIASES.projectId),
  storageBucket: getFirstDefinedEnv(ENV_ALIASES.storageBucket),
  messagingSenderId: getFirstDefinedEnv(ENV_ALIASES.messagingSenderId),
  appId: getFirstDefinedEnv(ENV_ALIASES.appId),
  measurementId: getFirstDefinedEnv(ENV_ALIASES.measurementId),
  /** Optional — required for browser push notifications (FCM getToken) */
  vapidKey: getFirstDefinedEnv(ENV_ALIASES.vapidKey),
  /** Production log threshold: DEBUG | INFO | WARN | ERROR. Defaults to WARN when empty. */
  logLevel: getFirstDefinedEnv(ENV_ALIASES.logLevel),
  cryptoSecret: getFirstDefinedEnv(ENV_ALIASES.cryptoSecret),
  sentryDsn: getFirstDefinedEnv(ENV_ALIASES.sentryDsn),
  sentryEnvironment: getFirstDefinedEnv(ENV_ALIASES.sentryEnvironment),
  sentryRelease: getFirstDefinedEnv(ENV_ALIASES.sentryRelease),
  commitSha: getFirstDefinedEnv(ENV_ALIASES.commitSha),
  razorpayKeyId: getFirstDefinedEnv(ENV_ALIASES.razorpayKeyId),
  razorpayOrderUrl: getFirstDefinedEnv(ENV_ALIASES.razorpayOrderUrl),
  razorpayVerifyUrl: getFirstDefinedEnv(ENV_ALIASES.razorpayVerifyUrl),
  publicContactUrl: getFirstDefinedEnv(ENV_ALIASES.publicContactUrl),
  refreshLandingSummaryUrl: getFirstDefinedEnv(ENV_ALIASES.refreshLandingSummaryUrl),
  writerCheckoutUrl,
};
